import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PdfUploadPanel from './components/PdfUploadPanel';
import PdfCaptureViewer from './components/PdfCaptureViewer';
import NotesManager from './components/NotesManager';
import { createEmptyNote, type NoteRecord } from '@/types/note';
import { useNotes } from '@/hooks/useNotes';
import { collectStudentNames } from '@/utils/noteGroups';
import { useAuth } from '@/contexts/AuthContext';
import { loadPdfPages, type RenderedPage } from '@/utils/pdfCapture';

type TabKey = 'capture' | 'notes';

/** 일정 시간이 지나도 안 끝나면 에러로 처리 (무한 로딩 방지) */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

export default function Home() {
  const { user, profile, signOut } = useAuth();
  const isTeacher = profile?.role !== 'student';
  const {
    notes,
    saveNote,
    deleteNote,
    deleteMany,
    clearNotes,
    loading: notesLoading,
    error: notesError,
    refresh: refreshNotes,
  } = useNotes(user?.id ?? null);

  const [pdfName, setPdfName] = useState<string | null>(null);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [studentName, setStudentName] = useState('');
  const [examName, setExamName] = useState('');
  const [subject, setSubject] = useState('수학');
  const [examType, setExamType] = useState('수능');
  const [tab, setTab] = useState<TabKey>('capture');
  const [capturedCount, setCapturedCount] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const handleFileSelected = useCallback(async (file: File) => {
    setLoadingPdf(true);
    setPdfError(null);
    setPages([]);
    setPdfName(file.name);
    setExamName((prev) => prev || file.name.replace(/\.(pdf|PDF)$/, ''));
    try {
      const loaded = await withTimeout(
        loadPdfPages(file),
        40000,
        '시간이 너무 오래 걸려요. 파일이 너무 크거나 이 브라우저가 PDF 처리를 막고 있어요.',
      );
      if (loaded.length === 0) throw new Error('페이지를 찾을 수 없음');
      setPages(loaded);
      setToast(`PDF ${loaded.length}페이지를 열었어요. 문제 영역을 드래그해 보세요.`);
    } catch (err) {
      console.error('[PDF load failed]', err);
      const detail = err instanceof Error ? err.message : String(err);
      setPdfError(`PDF를 열지 못했어요. 다른 PDF로 다시 시도해 주세요. (원인: ${detail})`);
    } finally {
      setLoadingPdf(false);
    }
  }, []);

  const handleClearPdf = useCallback(() => {
    setPdfName(null);
    setPages([]);
    setPdfError(null);
  }, []);

  const handleCapture = useCallback(
    (dataUrl: string, pageIndex: number) => {
      if (isTeacher && !studentName.trim()) {
        setToast('학생 이름을 먼저 입력해 주세요');
        return;
      }
      const note = createEmptyNote();
      note.problemImage = dataUrl;
      note.studentName = isTeacher ? studentName.trim() : '';
      note.examName = examName.trim();
      note.subject = subject;
      note.examType = examType;
      note.problemText = `${pageIndex + 1}페이지 캡처`;
      saveNote(note);
      setCapturedCount((c) => c + 1);
      setToast(isTeacher ? `${studentName.trim()} 학생의 오답노트에 추가했어요` : '캡처한 문제를 오답노트에 추가했어요');
    },
    [examName, isTeacher, studentName, subject, examType, saveNote],
  );

  const handlePatchNote = (id: string, patch: Partial<NoteRecord>) => {
    const target = notes.find((n) => n.id === id);
    if (!target) return;
    saveNote({ ...target, ...patch });
  };

  const handleDelete = (id: string) => {
    deleteNote(id);
    setToast('오답노트를 삭제했어요');
  };

  const handleDeleteExam = (ids: string[]) => {
    deleteMany(ids);
    setToast('해당 시험의 오답노트를 삭제했어요');
  };

  const handleClearAll = () => {
    if (notes.length === 0) return;
    clearNotes();
    setCapturedCount(0);
    setToast('모든 오답노트를 삭제했어요');
  };

  const sortedNotes = useMemo(
    () => [...notes].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [notes],
  );

  const studentSuggestions = useMemo(() => collectStudentNames(notes), [notes]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const tabClass = (key: TabKey) =>
    `flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-bold transition ${
      tab === key
        ? 'bg-primary-500 text-background-50'
        : 'text-foreground-600 hover:bg-background-200'
    }`;

  return (
    <div className="min-h-screen">
      {/* 헤더 */}
      <header className="print-hide sticky top-0 z-20 border-b border-background-200 bg-background-50/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-pencil-ruler-2-line text-lg"></i>
            </span>
            <div className="leading-tight">
              <h1 className="font-heading text-base font-bold text-foreground-950">오답노트 메이커</h1>
              <p className="text-[11px] text-foreground-500">PDF에서 문제 영역을 끌어 캡처 · A4 오답노트</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {profile?.role === 'admin' && (
              <Link
                to="/admin"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm font-semibold text-secondary-900 transition hover:bg-secondary-100"
              >
                <i className="ri-shield-user-line text-base"></i>
                <span className="hidden md:inline">회원 관리</span>
              </Link>
            )}
            <div className="hidden max-w-[180px] items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-2 lg:flex">
              <i className="ri-user-3-line text-sm text-foreground-500"></i>
              <span className="truncate text-xs font-medium text-foreground-700">{user?.email}</span>
            </div>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="로그아웃"
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-100"
            >
              <i className="ri-logout-box-r-line text-base"></i>
            </button>
          </div>
        </div>
      </header>

      {/* 워크스페이스 */}
      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
        {/* 탭 */}
        <div className="print-hide mb-5 flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1 sm:w-fit">
          <button type="button" onClick={() => setTab('capture')} className={tabClass('capture')}>
            <i className="ri-crop-2-line text-base"></i>
            PDF에서 캡처
          </button>
          <button type="button" onClick={() => setTab('notes')} className={tabClass('notes')}>
            <i className="ri-file-text-line text-base"></i>
            오답노트
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                tab === 'notes' ? 'bg-background-50/25 text-background-50' : 'bg-secondary-100 text-secondary-800'
              }`}
            >
              {notes.length}
            </span>
          </button>
        </div>

        {tab === 'capture' ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
            {/* 좌측: 업로드/설정 패널 */}
            <div className="print-hide lg:sticky lg:top-20 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-white p-5">
                <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-foreground-900">
                  <i className="ri-settings-3-line text-base text-primary-600"></i>
                  시험지 준비
                </h2>
                <PdfUploadPanel
                  isTeacher={isTeacher}
                  studentName={studentName}
                  studentSuggestions={studentSuggestions}
                  examName={examName}
                  subject={subject}
                  examType={examType}
                  onExamChange={(patch) => {
                    if (patch.studentName !== undefined) setStudentName(patch.studentName);
                    if (patch.examName !== undefined) setExamName(patch.examName);
                    if (patch.subject !== undefined) setSubject(patch.subject);
                    if (patch.examType !== undefined) setExamType(patch.examType);
                  }}
                  onFileSelected={handleFileSelected}
                  onErrorChange={setPdfError}
                  onClearPdf={handleClearPdf}
                  fileName={pdfName}
                  loading={loadingPdf}
                  error={pdfError}
                  capturedCount={capturedCount}
                  noteCount={notes.length}
                  onGoToNotes={() => setTab('notes')}
                />
              </div>
            </div>

            {/* 우측: PDF 뷰어 */}
            <div className="min-w-0">
              {loadingPdf ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-background-200 bg-white px-6 py-20 text-center">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-600"></i>
                  <p className="text-sm font-bold text-foreground-800">시험지를 여는 중이에요...</p>
                  <p className="text-xs text-foreground-500">파일이 크면 조금 걸릴 수 있어요.</p>
                </div>
              ) : pdfError ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-accent-200 bg-accent-50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-error-warning-line text-2xl"></i>
                  </span>
                  <p className="text-sm font-bold text-accent-800">PDF를 열지 못했어요</p>
                  <p className="max-w-[560px] break-words text-xs leading-relaxed text-accent-700">{pdfError}</p>
                  <p className="text-[11px] text-foreground-500">
                    위쪽 업로드 칸에서 다른 PDF로 다시 시도해 주세요.
                  </p>
                </div>
              ) : pages.length > 0 ? (
                <PdfCaptureViewer pages={pages} onCapture={handleCapture} />
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                    <i className="ri-file-pdf-2-line text-2xl"></i>
                  </span>
                  <p className="text-sm font-bold text-foreground-800">여기에 시험지가 열려요</p>
                  <p className="text-xs leading-relaxed text-foreground-500">
                    왼쪽(폰에서는 위)에서 PDF를 업로드하면 시험지가 이 자리에 표시됩니다.
                    <br />
                    문제 위에서 손가락(마우스)으로 사각형을 그리면 바로 캡처돼요.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            {notesError && (
              <div className="print-hide mb-4 flex items-center justify-between gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
                <p className="flex items-center gap-2 text-sm text-accent-800">
                  <i className="ri-error-warning-line text-base"></i>
                  {notesError}
                </p>
                <button
                  type="button"
                  onClick={() => void refreshNotes()}
                  className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-3 py-1.5 text-xs font-bold text-accent-800 transition hover:bg-accent-100"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  다시 시도
                </button>
              </div>
            )}
            {notesLoading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
                <p className="text-sm text-foreground-500">오답노트를 불러오는 중이에요...</p>
              </div>
            ) : notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-book-open-line text-2xl"></i>
                </span>
                <p className="text-sm font-bold text-foreground-800">아직 만든 오답노트가 없어요</p>
                <p className="text-xs leading-relaxed text-foreground-500">
                  'PDF에서 캡처' 탭에서 PDF를 올리고
                  <br />
                  문제 영역을 드래그해 보세요.
                </p>
                <button
                  type="button"
                  onClick={() => setTab('capture')}
                  className="mt-1 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
                >
                  <i className="ri-crop-2-line text-base"></i>
                  PDF에서 캡처하러 가기
                </button>
              </div>
            ) : (
              <NotesManager
                notes={sortedNotes}
                isTeacher={isTeacher}
                ownerLabel="내 오답노트"
                onChangeNote={handlePatchNote}
                onDeleteNote={handleDelete}
                onDeleteNotes={handleDeleteExam}
                onClearAll={handleClearAll}
              />
            )}
          </div>
        )}
      </main>

      {/* 푸터 */}
      <footer className="print-hide border-t border-background-200 bg-secondary-50">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-2 px-4 py-5 text-center sm:flex-row sm:px-6 sm:text-left">
          <p className="text-xs text-foreground-500">오답노트 메이커 · 수험생을 위한 수학 오답 정리 도구</p>
          <p className="text-xs text-foreground-400">복습이 성적을 만듭니다.</p>
        </div>
      </footer>

      {/* 토스트 */}
      {toast && (
        <div className="print-hide fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <div className="flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-900 px-4 py-2.5 text-sm font-medium text-background-50">
            <i className="ri-checkbox-circle-line text-base text-primary-300"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}